'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { DynamicStrengthIndexCardPropTypes } from './DynamicStrengthIndexCard.types';

const units: UnitTypes[] = [
  { id: 'newtons', name: 'Newtons', unit: 'N' },
  { id: 'pounds', name: 'Pounds', unit: 'lb' },
  { id: 'kilograms', name: 'Kilograms', unit: 'kg' },
];

const DynamicStrengthIndexCard: React.FC<DynamicStrengthIndexCardPropTypes> = () => {
  const isMobile = useIsMobile();
  const [unit, setUnit] = React.useState<string>(units[0].id);

  // Main values used for calculation
  const [cmjValue, setCmjValue] = React.useState('');
  const [imtpValue, setImtpValue] = React.useState('');

  // Temporary values for mobile
  const [tempCmjValue, setTempCmjValue] = React.useState('');
  const [tempImtpValue, setTempImtpValue] = React.useState('');

  const handleCalculate = () => {
    // Only update the main values when Calculate is clicked (mobile)
    setCmjValue(tempCmjValue);
    setImtpValue(tempImtpValue);
  };

  const convertToNewtons = (value: string, fromUnit: string) => {
    const v = parseFloat(value);
    if (isNaN(v)) return 0;
    switch (fromUnit) {
      case 'pounds':
        return v / 0.224809;
      case 'kilograms':
        return v / 0.1019716;
      default:
        return v; // newtons
    }
  };

  // Always use main values for calculation
  const cmjInNewtons = convertToNewtons(cmjValue, unit);
  const imtpInNewtons = convertToNewtons(imtpValue, unit);

  let dsi = 0;
  if (imtpInNewtons !== 0 && !isNaN(imtpInNewtons)) {
    dsi = cmjInNewtons / imtpInNewtons;
  }
  const formattedDSI = dsi.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formattedDSI by 3000 ms ---
  const debouncedDSI = useDebounce(formattedDSI, 3000);

  // Fire a tracking event whenever the debounced DSI settles to a valid number
  React.useEffect(() => {
    if (
      cmjValue !== '' &&
      imtpValue !== '' &&
      !isNaN(Number(debouncedDSI)) &&
      isFinite(Number(debouncedDSI))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_dynamic_strength_index',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedDSI changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDSI]);

  return (
    <CardMetric
      id='calculator-dynamic-strength-index'
      className='mt-[2.4rem] lg:mt-[4rem] xl:mt-[unset]'
      title='DSI'
      productIcons={[
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        The <strong className='font-medium'>Dynamic Strength Index</strong> is a ratio of ballistic
        strength and maximal isometric strength that can be used to guide programming decisions.
      </p>

      <FractionDisplay
        className='mb-[2rem]'
        label='Dynamic Strength Index'
        numerator='<strong className="font-medium">Countermovement Jump</strong> Takeoff Peak Force'
        denominator='<strong className="font-medium">Isometric Mid-Thigh Pull</strong> Peak Vertical Force'
      />

      {/* Shared Unit Dropdown */}
      <UnitDropdown units={units} activeUnitId={unit} onChange={setUnit} />

      {/* Inputs */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Countermovement Jump:', 'Takeoff Peak Force']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempCmjValue : cmjValue}
        onChange={(e) => {
          if (isMobile) {
            setTempCmjValue(e.target.value); // Store temporarily on mobile
          } else {
            setCmjValue(e.target.value); // Update directly on desktop
          }
        }}
      />
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Isometric Mid-Thigh Pull:', 'Peak Vertical Force']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempImtpValue : imtpValue}
        onChange={(e) => {
          if (isMobile) {
            setTempImtpValue(e.target.value); // Store temporarily on mobile
          } else {
            setImtpValue(e.target.value); // Update directly on desktop
          }
        }}
      />

      {/* DSI Result with unit */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Dynamic Strength Index</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isNaN(dsi) || !isFinite(dsi) ? '0.0' : formattedDSI}
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href='https://valdperformance.com/news/research-summary-what-is-the-dynamic-strength-index'
            >
              Learn more
            </NextLink>{' '}
            about DSI research, application and how to generate a DSI report in VALD Hub.
          </p>
        </div>
        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={tempCmjValue === cmjValue && tempImtpValue === imtpValue}
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default DynamicStrengthIndexCard;
